import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CustomButton, colors } from "@ippon/shared-components";
import {
  SortableTextRow,
  restrictToVerticalAxis,
  restrictToParentElement,
  type PulldownEditorHandle,
} from "./pulldownSortable";

interface OptionListEditorProps {
  /** 補足説明 (任意) */
  description?: string;
  /** サーバ保存済みの選択肢 (表示順 = 配列順) */
  options: string[];
  /** コミット中 (下部更新ボタン押下中) は操作を抑制する */
  disabled?: boolean;
  /** 追加ボタンのラベル */
  addLabel?: string;
  /** dirty 状態が変わったら親へ通知 (下部更新ボタンの活性制御用) */
  onDirtyChange?: (dirty: boolean) => void;
  /** 編集後の選択肢配列(順序込み・空除去・重複除去済み)をコミット。失敗時はメッセージ、成功時は null。 */
  onSave: (next: string[]) => Promise<string | null>;
  /** 並び替え確定時の即時保存 (純粋な並べ替え時のみ呼ばれる)。配列順=新しい表示順。 */
  onReorder?: (next: string[]) => void;
}

/**
 * 文字列配列マスタ (グループ / 修繕カテゴリ / 設備種別 / 関連法令) を
 * 追加・リネーム・削除・並べ替えできる汎用エディタ。
 * カスタムフォームの追加項目 UI (DnD 行 + ダッシュの追加ボタン) を踏襲する。
 *
 * - 保存ボタンは持たず、下部 FixedActionBar の「更新」から親が save() を呼ぶ
 *   (forwardRef + useImperativeHandle で公開)。
 * - 値は react-hook-form で行 id をキーに管理 (CustomForm と同じ流儀)。
 * - これらはフリーテキストの選択肢集合なので、選択肢を消しても
 *   過去レコードが保持している値 (スナップショット) は壊れない。
 */
const OptionListEditor = forwardRef<PulldownEditorHandle, OptionListEditorProps>(
  function OptionListEditor(
    {
      description,
      options,
      disabled,
      addLabel = "新しい選択肢を追加",
      onDirtyChange,
      onSave,
      onReorder,
    },
    ref,
  ) {
    const { register, getValues, setValue, reset, watch } = useForm<
      Record<string, string>
    >({ defaultValues: {} });
    const [rows, setRows] = useState<{ id: string }[]>([]);
    const counterRef = useRef(0);
    const sensors = useSensors(
      useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    );

    // 親が `record?.groups ?? []` のように毎レンダリングで新しい配列参照を渡しても
    // 再発火しないよう、配列「内容」をキーにする (参照比較だと無限ループになる)。
    const optionsKey = JSON.stringify(options);
    useEffect(() => {
      const initial = options.map((_, i) => ({ id: `row_${i}` }));
      const values: Record<string, string> = {};
      initial.forEach((r, i) => {
        values[r.id] = options[i];
      });
      reset(values);
      setRows(initial);
      counterRef.current = options.length;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [optionsKey, reset]);

    const watched = watch();

    const handleAdd = () => {
      const id = `row_new_${counterRef.current++}`;
      setRows((prev) => [...prev, { id }]);
      setValue(id, "");
    };
    const handleDelete = (id: string) =>
      setRows((prev) => prev.filter((r) => r.id !== id));
    const handleDragEnd = (e: DragEndEvent) => {
      const { active, over } = e;
      if (!over || active.id === over.id) return;
      setRows((prev) => {
        const oldIndex = prev.findIndex((r) => r.id === active.id);
        const newIndex = prev.findIndex((r) => r.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return prev;
        const next = arrayMove(prev, oldIndex, newIndex);
        // 純粋な並べ替え (未保存の追加/改名/削除が無い) なら即時保存。
        // 未保存の編集があるときは「更新」に委譲 (更新時に現在の順序で保存される)。
        const optSet = new Set(options);
        const uniq = [
          ...new Set(
            next
              .map((r) => (getValues(r.id) ?? "").trim())
              .filter((v) => v.length > 0),
          ),
        ];
        const pureReorder =
          uniq.length === options.length && uniq.every((v) => optSet.has(v));
        if (pureReorder) onReorder?.(uniq);
        return next;
      });
    };

    const currentValues = rows
      .map((r) => (watched[r.id] ?? getValues(r.id) ?? "").trim())
      .filter((v) => v.length > 0);
    // 並び替えは即時保存するため dirty (更新ボタン) には含めない。
    // 集合差 (追加/改名/削除) があるときだけ dirty とする。
    const curUniq = [...new Set(currentValues)];
    const optSet = new Set(options);
    const dirty =
      curUniq.length !== options.length ||
      curUniq.some((v) => !optSet.has(v));

    // dirty の変化を親へ通知 (下部更新ボタンの活性制御)
    useEffect(() => {
      onDirtyChange?.(dirty);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dirty]);

    // 重複除去した確定配列
    const buildNext = (): string[] => {
      const seen = new Set<string>();
      const next: string[] = [];
      for (const v of rows
        .map((r) => (getValues(r.id) ?? "").trim())
        .filter((v) => v.length > 0)) {
        if (!seen.has(v)) {
          seen.add(v);
          next.push(v);
        }
      }
      return next;
    };

    useImperativeHandle(
      ref,
      () => ({
        isDirty: () => dirty,
        save: async () => {
          if (!dirty) return [];
          const err = await onSave(buildNext());
          return err ? [err] : [];
        },
        pendingDeletions: () => {
          const cur = new Set(buildNext());
          return options.filter((o) => !cur.has(o));
        },
      }),
      // dirty / rows / options / onSave が変われば最新の save を公開
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [dirty, rows, optionsKey, onSave],
    );

    return (
      <Box>
        {description && (
          <Typography
            variant="body2"
            sx={{
              mb: 1.5,
              color: colors.text.secondary,
              fontSize: { xs: 12, sm: 14 },
            }}
          >
            {description}
          </Typography>
        )}
        {rows.length > 0 && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={rows.map((r) => r.id)}
              strategy={verticalListSortingStrategy}
            >
              <Stack spacing={1} useFlexGap sx={{ mb: 1 }}>
                {rows.map((r) => (
                  <SortableTextRow
                    key={r.id}
                    id={r.id}
                    register={register}
                    onDelete={() => handleDelete(r.id)}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
        )}
        <CustomButton
          label={addLabel}
          buttonCategory="clearPrimary"
          onClick={handleAdd}
          disabled={!!disabled}
          sx={{ width: "100%", borderStyle: "dashed" }}
        />
      </Box>
    );
  },
);

export default OptionListEditor;
